import { type ChangeEvent, type FC, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import type { ConsoleOutputLine, GenerationStatus } from '../../types/generation';
import styles from './BusinessFlow.module.css';

interface GenerationPanelProps {
  activeSpec: any;
  loading: boolean;
  generating: boolean;
  uploading: boolean;
  consoleOutput: ConsoleOutputLine[];
  generationStatus: GenerationStatus | null;
  onGenerate: () => void;
  onStop: () => void;
  onUploadCollection: (parsed: any) => Promise<void>;
}

export const GenerationPanel: FC<GenerationPanelProps> = ({
  activeSpec,
  loading,
  generating,
  uploading,
  consoleOutput,
  generationStatus,
  onGenerate,
  onStop,
  onUploadCollection,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const consoleRef = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: consoleOutput.length triggers auto-scroll
  useEffect(() => {
    if (consoleRef.current) {
      consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
    }
  }, [consoleOutput.length]);

  const handleUploadCollection = () => {
    fileInputRef.current?.click();
  };

  const onFileSelected = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      await onUploadCollection(parsed);
    } catch {
      toast.error('Invalid JSON file');
    }
  };

  const hasConsole = generating || consoleOutput.length > 0;

  return (
    <div className={styles.generateSection}>
      <div className={styles.generateHeader} data-has-console={hasConsole ? 'true' : 'false'}>
        <h2 className={styles.generateTitle}>Generate Postman Collection</h2>
        <div className={styles.buttonGroup}>
          {generating ? (
            <button className={styles.button} onClick={onStop}>
              Stop Generation
            </button>
          ) : (
            <button className={styles.button} onClick={onGenerate} disabled={!activeSpec || loading}>
              Generate Collection
            </button>
          )}
          <button
            className={styles.button}
            onClick={handleUploadCollection}
            disabled={!activeSpec || uploading || generating}
          >
            Upload Collection
          </button>
        </div>
      </div>
      <input type="file" accept=".json" ref={fileInputRef} onChange={onFileSelected} style={{ display: 'none' }} />

      {hasConsole && (
        <div className={styles.console} ref={consoleRef}>
          {consoleOutput.map((line, index) => (
            <div className={styles.consoleLine} key={index}>
              {line.data}
            </div>
          ))}
          {generationStatus && (
            <div className={styles.consoleStatus} data-status={generationStatus}>
              {generationStatus === 'running' && 'Generation in progress...'}
              {generationStatus === 'success' && 'Generation completed successfully'}
              {generationStatus === 'error' && 'Generation failed'}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
